import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Tag,
  Descriptions,
  Select,
  Card,
  Badge,
} from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { TextArea } = Input;

const SolicitudesClaseAlumno = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalObservacionesVisible, setModalObservacionesVisible] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Cargar solicitudes
  const loadSolicitudes = async () => {
    setLoading(true);
    try {
      const url = filtroEstado 
        ? `/solicitudes-clase?estado=${filtroEstado}`
        : '/solicitudes-clase';
      const response = await apiClient.get(url);
      if (response.ok) {
        const data = await response.json();
        setSolicitudes(data);
      } else {
        message.error('Error al cargar las solicitudes');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSolicitudes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ver detalle de solicitud
  const handleView = (record) => {
    setSolicitudSeleccionada(record);
    setModalVisible(true);
  };

  // Actualizar estado de solicitud
  const handleUpdateEstado = async (id, nuevoEstado, observaciones = null) => {
    try {
      const dataToSend = {
        estado: nuevoEstado,
        ...(observaciones && { observaciones }),
      };

      const response = await apiClient.put(`/solicitudes-clase/${id}/estado`, dataToSend);
      if (response.ok) {
        message.success(`Solicitud ${nuevoEstado === 'APROBADA' ? 'aprobada' : 'rechazada'} correctamente`);
        loadSolicitudes();
        setModalVisible(false);
        setModalObservacionesVisible(false);
        form.resetFields();
        setSolicitudSeleccionada(null);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al actualizar la solicitud');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al actualizar la solicitud');
    }
  };

  // Abrir modal para agregar observaciones y cambiar estado
  const handleCambiarEstado = (solicitud, nuevoEstado) => {
    setSolicitudSeleccionada(solicitud);
    form.setFieldsValue({
      observaciones: solicitud.sca_observaciones || '',
    });
    setModalObservacionesVisible(true);
    form.setFieldValue('nuevoEstado', nuevoEstado);
  };

  // Guardar observaciones y cambiar estado
  const handleGuardarObservaciones = async () => {
    try {
      const values = await form.validateFields();
      const nuevoEstado = form.getFieldValue('nuevoEstado');
      await handleUpdateEstado(solicitudSeleccionada.sca_id, nuevoEstado, values.observaciones);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Eliminar solicitud
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/solicitudes-clase/${id}`);
      if (response.ok) {
        message.success('Solicitud eliminada correctamente');
        loadSolicitudes();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la solicitud');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la solicitud');
    }
  };

  // Obtener color del tag según estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'PENDIENTE':
        return 'orange';
      case 'APROBADA':
        return 'green';
      case 'RECHAZADA':
        return 'red';
      default:
        return 'default';
    }
  };

  // Obtener día de la semana
  const getDiaSemana = (dia) => {
    const dias = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    return dias[dia] || '';
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Alumno',
      key: 'alumno',
      render: (_, record) => `${record.alu_nombre} ${record.alu_apellido}`,
    },
    {
      title: 'Email',
      dataIndex: 'alu_email',
      key: 'alu_email',
      render: (email) => email || '-',
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_descripcion',
      key: 'categoria_descripcion',
      render: (descripcion) => descripcion || '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'tur_tipo',
      key: 'tur_tipo',
      render: (tipo) => (
        <Tag color={tipo === 'FIJO' ? 'blue' : 'purple'}>
          {tipo === 'FIJO' ? 'Fijo' : 'Evento'}
        </Tag>
      ),
    },
    {
      title: 'Fecha Clase',
      dataIndex: 'sca_fecha_clase',
      key: 'sca_fecha_clase',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Horario',
      key: 'horario',
      render: (_, record) => `${record.tur_hora_desde} - ${record.tur_hora_hasta}`,
    },
    {
      title: 'Cancha',
      dataIndex: 'cancha_descripcion',
      key: 'cancha_descripcion',
      render: (descripcion) => descripcion || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'sca_estado',
      key: 'sca_estado',
      width: 120,
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {estado}
        </Tag>
      ),
    },
    {
      title: 'Fecha Solicitud',
      dataIndex: 'sca_fecha_creacion',
      key: 'sca_fecha_creacion',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: isMobile ? 150 : 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          >
            Ver
          </Button>
          {record.sca_estado === 'PENDIENTE' && (
            <>
              <Popconfirm
                title="¿Aprobar esta solicitud?"
                onConfirm={() => handleCambiarEstado(record, 'APROBADA')}
                okText="Sí"
                cancelText="No"
              >
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  style={{ color: '#52c41a' }}
                  size="small"
                >
                  Aprobar
                </Button>
              </Popconfirm>
              <Popconfirm
                title="¿Rechazar esta solicitud?"
                onConfirm={() => handleCambiarEstado(record, 'RECHAZADA')}
                okText="Sí"
                cancelText="No"
              >
                <Button
                  type="link"
                  danger
                  icon={<CloseCircleOutlined />}
                  size="small"
                >
                  Rechazar
                </Button>
              </Popconfirm>
            </>
          )}
          <Popconfirm
            title="¿Eliminar esta solicitud?"
            onConfirm={() => handleDelete(record.sca_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <h1 style={{ 
            color: '#ffffff', 
            margin: 0,
            fontSize: isMobile ? '20px' : '24px',
          }}>
            <CalendarOutlined style={{ marginRight: '8px' }} />
            Solicitudes de Clase de Alumnos
          </h1>
          <Space>
            <Select
              placeholder="Filtrar por estado"
              allowClear
              value={filtroEstado}
              onChange={(value) => setFiltroEstado(value)}
              style={{ width: 150 }}
            >
              <Select.Option value="PENDIENTE">Pendiente</Select.Option>
              <Select.Option value="APROBADA">Aprobada</Select.Option>
              <Select.Option value="RECHAZADA">Rechazada</Select.Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadSolicitudes}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        </div>

        {/* Badge con contador de pendientes */}
        {solicitudes.filter(s => s.sca_estado === 'PENDIENTE').length > 0 && (
          <Badge 
            count={solicitudes.filter(s => s.sca_estado === 'PENDIENTE').length} 
            style={{ marginBottom: '16px' }}
          >
            <Tag color="orange" style={{ fontSize: '14px', padding: '4px 12px' }}>
              Solicitudes pendientes: {solicitudes.filter(s => s.sca_estado === 'PENDIENTE').length}
            </Tag>
          </Badge>
        )}

        <Table
          columns={columns}
          dataSource={solicitudes}
          loading={loading}
          rowKey="sca_id"
          scroll={isMobile ? { x: 'max-content' } : undefined}
          pagination={{
            pageSize: isMobile ? 10 : 20,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} solicitudes`,
          }}
        />

        {/* Modal para ver detalles */}
        <Modal
          title="Detalles de la Solicitud"
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setSolicitudSeleccionada(null);
          }}
          footer={null}
          width={isMobile ? '95%' : 700}
        >
          {solicitudSeleccionada && (
            <div>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Alumno">
                  {solicitudSeleccionada.alu_nombre} {solicitudSeleccionada.alu_apellido}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {solicitudSeleccionada.alu_email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Estado">
                  <Tag color={getEstadoColor(solicitudSeleccionada.sca_estado)}>
                    {solicitudSeleccionada.sca_estado}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Categoría">
                  {solicitudSeleccionada.categoria_descripcion || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo de Turno">
                  <Tag color={solicitudSeleccionada.tur_tipo === 'FIJO' ? 'blue' : 'purple'}>
                    {solicitudSeleccionada.tur_tipo === 'FIJO' ? 'Fijo' : 'Evento'}
                  </Tag>
                </Descriptions.Item>
                {solicitudSeleccionada.tur_tipo === 'FIJO' && (
                  <Descriptions.Item label="Día de la Semana">
                    {getDiaSemana(solicitudSeleccionada.tur_dia_semana)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Fecha de la Clase">
                  {dayjs(solicitudSeleccionada.sca_fecha_clase).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Horario">
                  {solicitudSeleccionada.tur_hora_desde} - {solicitudSeleccionada.tur_hora_hasta}
                </Descriptions.Item>
                <Descriptions.Item label="Cancha">
                  {solicitudSeleccionada.cancha_descripcion || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Profesor">
                  {solicitudSeleccionada.profesor_nombre && solicitudSeleccionada.profesor_apellido
                    ? `${solicitudSeleccionada.profesor_nombre} ${solicitudSeleccionada.profesor_apellido}`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Capacidad Máxima">
                  {solicitudSeleccionada.tur_cantidad_alumnos || '-'} alumnos
                </Descriptions.Item>
                <Descriptions.Item label="Fecha de Solicitud">
                  {dayjs(solicitudSeleccionada.sca_fecha_creacion).format('DD/MM/YYYY HH:mm')}
                </Descriptions.Item>
                {solicitudSeleccionada.sca_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {solicitudSeleccionada.sca_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>

              {solicitudSeleccionada.sca_estado === 'PENDIENTE' && (
                <div style={{ marginTop: '24px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => handleCambiarEstado(solicitudSeleccionada, 'APROBADA')}
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Aprobar
                  </Button>
                  <Button
                    onClick={() => handleCambiarEstado(solicitudSeleccionada, 'RECHAZADA')}
                    danger
                    icon={<CloseCircleOutlined />}
                  >
                    Rechazar
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Modal para agregar observaciones */}
        <Modal
          title={solicitudSeleccionada && form.getFieldValue('nuevoEstado') === 'APROBADA' 
            ? 'Aprobar Solicitud' 
            : 'Rechazar Solicitud'}
          open={modalObservacionesVisible}
          onOk={handleGuardarObservaciones}
          onCancel={() => {
            setModalObservacionesVisible(false);
            form.resetFields();
            setSolicitudSeleccionada(null);
          }}
          okText="Confirmar"
          cancelText="Cancelar"
          width={isMobile ? '95%' : 500}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="observaciones"
              label="Observaciones (opcional)"
            >
              <TextArea
                rows={4}
                placeholder="Agregar observaciones sobre esta solicitud..."
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default SolicitudesClaseAlumno;






